import {
  type ChangeEvent,
  type DragEvent,
  type ReactElement,
  type ReactNode,
  useRef,
  useState,
} from 'react'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { Box, CircularProgress, useTheme } from '@mui/material'

export interface FileDropzoneProps {
  onFilesSelected: (files: File[]) => void
  /** Native input accept attribute, e.g. '.ifc' or '.pdf,application/pdf'. */
  accept?: string
  multiple?: boolean
  disabled?: boolean
  /** Shows a spinner and blocks further picks while an upload is running. */
  isUploading?: boolean
  /** Replaces the idle headline, e.g. with the name of the file already picked. */
  title?: string
  /** Line below the headline; defaults to the "oder durchsuchen" hint. */
  hint?: ReactNode
}

const DRAG_BACKGROUND = 'rgba(55,132,52,0.08)'
const HOVER_BACKGROUND = 'rgba(55,132,52,0.04)'

/**
 * Dashed drop area that takes files by drag & drop or by opening the file
 * picker on click. Purely presentational: what happens to the files is the
 * caller's business, which is why it works both for an upload that starts
 * right away (Results2 documents) and for a form field holding one file.
 */
const FileDropzone = ({
  onFilesSelected,
  accept,
  multiple = false,
  disabled = false,
  isUploading = false,
  title,
  hint,
}: FileDropzoneProps): ReactElement => {
  const theme = useTheme()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // Counts enter/leave of nested children, which each fire their own events --
  // without it the highlight drops as soon as the pointer crosses the icon.
  const dragDepthRef = useRef(0)
  const [isDraggingFile, setIsDraggingFile] = useState(false)

  const isBlocked = disabled || isUploading

  const selectFiles = (selectedFiles: FileList | null) => {
    if (isBlocked || !selectedFiles?.length) return
    const files = Array.from(selectedFiles)
    // The multiple flag only constrains the picker dialog, not a drop -- a
    // multi-file drop on a single-file zone delivers just the first file.
    onFilesSelected(multiple ? files : files.slice(0, 1))
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFiles(event.target.files)
    // Lets the same file be picked again after it was removed.
    event.target.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current = 0
    setIsDraggingFile(false)
    selectFiles(event.dataTransfer.files)
  }

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current += 1
    if (!isBlocked) {
      setIsDraggingFile(true)
    }
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) {
      setIsDraggingFile(false)
    }
  }

  const headline = title
    ? title
    : isUploading
    ? multiple
      ? 'Dateien werden hochgeladen...'
      : 'Datei wird hochgeladen...'
    : isDraggingFile
    ? multiple
      ? 'Dateien loslassen zum Hochladen'
      : 'Datei loslassen zum Hochladen'
    : multiple
    ? 'Dateien hierher ziehen'
    : 'Datei hierher ziehen'

  return (
    <Box
      onClick={() => fileInputRef.current?.click()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={event => {
        event.preventDefault()
        event.dataTransfer.dropEffect = isBlocked ? 'none' : 'copy'
      }}
      onDrop={handleDrop}
      sx={{
        border: `1.5px dashed ${theme.palette.divider}`,
        borderRadius: 1,
        px: 1.5,
        py: 2.25,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        color: 'text.secondary',
        fontSize: 12,
        cursor: isBlocked ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        background: isDraggingFile ? DRAG_BACKGROUND : theme.palette.grey[50],
        borderColor: isDraggingFile ? theme.palette.primary.main : theme.palette.divider,
        boxShadow: isDraggingFile ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
        transition: 'background 150ms, border-color 150ms, box-shadow 150ms',
        '&:hover': {
          background: isBlocked ? undefined : HOVER_BACKGROUND,
          borderColor: isBlocked ? theme.palette.divider : theme.palette.primary.main,
        },
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        hidden
        accept={accept}
        disabled={isBlocked}
        onChange={handleInputChange}
      />
      {isUploading ? (
        <Box p={0.5} display="flex">
          <CircularProgress size={16} />
        </Box>
      ) : (
        <CloudUploadOutlinedIcon
          sx={{
            fontSize: 28,
            color: isDraggingFile ? theme.palette.primary.main : 'rgba(0,0,0,0.38)',
          }}
        />
      )}
      <Box sx={{ fontSize: 13, color: 'text.primary', textAlign: 'center' }}>{headline}</Box>
      <Box sx={{ fontSize: 11 }}>
        {hint ?? (
          <>
            oder{' '}
            <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
              durchsuchen
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default FileDropzone
